// ════════════════════════════════════════════════════════════════════════
// DNACore V1 LEGACY ONLY — Do not add new features here.
// Tela legada da listagem de Títulos. Toda nova funcionalidade deve ir
// em /app/frontend/src/pages/DnaPay/DnaPayTitulos.jsx (V2).
// Rota V1 /titles é redirecionada para /v2/financeiro/titulos em App.js.
// ════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState, useRef } from 'react';
import { ModuleHeader } from '@/design-system/dna';
import { useReactToPrint } from 'react-to-print';
import api, { titlesAPI, studentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Calculator, DollarSign, RefreshCw, Camera, Upload, CreditCard, Banknote, 
  QrCode, Search, Receipt, Eye, Edit, Trash2, RotateCcw, Building, Calendar,
  Percent, AlertTriangle, ChevronLeft, ChevronRight, Filter, Printer,
  Archive, Undo2, X, Loader2
} from 'lucide-react';
import { canEdit } from '@/lib/auth';
import { formatCurrency, formatDate, getTitleStatusLabel, getTitleStatusColor } from '@/lib/utils';
import ReciboImpressao from '@/components/ReciboImpressao';

export default function Titles() {
  const [titles, setTitles] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [mesFilter, setMesFilter] = useState('all');
  
  // Contas bancárias
  const [contasBancarias, setContasBancarias] = useState([]);
  
  // Pay dialog
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [paidValue, setPaidValue] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [contaBancariaId, setContaBancariaId] = useState('');
  const [taxaCartao, setTaxaCartao] = useState('');
  const [parcelasCartao, setParcelasCartao] = useState('1');
  const [receiptPhoto, setReceiptPhoto] = useState('');
  const [capturing, setCapturing] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Edit payment dialog
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  
  // View receipt dialog
  const [viewReceiptOpen, setViewReceiptOpen] = useState(false);
  const [viewingTitle, setViewingTitle] = useState(null);
  
  // Confirm estorno dialog
  const [estornoDialogOpen, setEstornoDialogOpen] = useState(false);
  const [estornoTitle, setEstornoTitle] = useState(null);

  // Recibo para impressão
  const [reciboDialogOpen, setReciboDialogOpen] = useState(false);
  const [reciboTitulo, setReciboTitulo] = useState(null);
  const [reciboAluno, setReciboAluno] = useState(null);
  const [escolaInfo, setEscolaInfo] = useState(null);
  const reciboRef = useRef(null);

  const [formData, setFormData] = useState({
    reference_month: '',
    due_day: 10,
    value: '',
    student_ids: []
  });

  // ─────────────────────────────────────────────────────────
  // Exclusão financeira segura (Soft Delete + Restore)
  // ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludedTitles, setExcludedTitles] = useState([]);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteScope, setDeleteScope] = useState('selected'); // selected | filter | student
  const [deleteReason, setDeleteReason] = useState('');
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteByStudentId, setDeleteByStudentId] = useState('');

  const paymentMethods = [
    { value: 'dinheiro', label: 'Dinheiro', icon: Banknote },
    { value: 'pix', label: 'PIX', icon: QrCode },
    { value: 'cartao_credito', label: 'Cartão de Crédito', icon: CreditCard },
    { value: 'cartao_debito', label: 'Cartão de Débito', icon: CreditCard },
    { value: 'transferencia', label: 'Transferência', icon: DollarSign },
    { value: 'boleto', label: 'Boleto', icon: Receipt },
  ];

  // Gerar lista de meses para filtro
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -12; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };
  const monthOptions = generateMonthOptions();

  useEffect(() => {
    loadData();
    loadContasBancarias();
    loadEscolaInfo();
  }, [statusFilter, mesFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (mesFilter) params.reference_month = mesFilter;
      
      const [titlesRes, studentsRes] = await Promise.all([
        titlesAPI.list(params),
        studentsAPI.list()
      ]);
      setTitles(titlesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadEscolaInfo = async () => {
    try {
      const res = await api.get('/fiscal/config');
      if (res.data) {
        setEscolaInfo({
          razao_social: res.data.razao_social || 'DNA HUB - Sistema Educacional',
          cnpj: res.data.cnpj || '',
          endereco: res.data.logradouro ? `${res.data.logradouro}, ${res.data.numero || 's/n'} - ${res.data.bairro || ''}` : '',
          cidade: res.data.cidade || '',
          uf: res.data.uf || 'SP',
          telefone: res.data.telefone || ''
        });
      }
    } catch (error) {
      // Usar dados padrão se não conseguir carregar
      setEscolaInfo({
        razao_social: 'DNA HUB - Sistema Educacional',
        cnpj: '',
        endereco: '',
        cidade: '',
        uf: 'SP',
        telefone: ''
      });
    }
  };

  const loadContasBancarias = async () => {
    try {
      const res = await api.get('/contas-bancarias');
      setContasBancarias(res.data.contas || []);
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error);
    }
  };

  const handleGenerateMonthly = async (e) => {
    e.preventDefault();
    try {
      const selectedStudentIds = students.filter(s => s.active).map(s => s.id);
      await titlesAPI.generateMonthly({
        ...formData,
        value: parseFloat(formData.value),
        student_ids: selectedStudentIds
      });
      toast.success('Mensalidades geradas com sucesso!');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar mensalidades');
    }
  };

  const handleRecalculateAll = async () => {
    try {
      await titlesAPI.recalculateAll();
      toast.success('Juros recalculados!');
      loadData();
    } catch (error) {
      toast.error('Erro ao recalcular');
    }
  };

  const openPayDialog = (title) => {
    setSelectedTitle(title);
    const valorSugerido = title.original_value - (title.discount_value || 0) + (title.fine_value || 0) + (title.interest_value || 0);
    setPaidValue(valorSugerido.toString());
    setPaymentMethod('dinheiro');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentNotes('');
    setContaBancariaId('');
    setTaxaCartao('');
    setParcelasCartao('1');
    setReceiptPhoto('');
    setPayDialogOpen(true);
  };

  const openEditPaymentDialog = (title) => {
    setEditingTitle(title);
    setPaidValue(title.paid_value?.toString() || '');
    setPaymentMethod(title.payment_method || 'dinheiro');
    setPaymentDate(title.paid_at ? title.paid_at.split('T')[0] : new Date().toISOString().split('T')[0]);
    setPaymentNotes(title.observacao_pagamento || '');
    setContaBancariaId(title.conta_bancaria?.id || '');
    setTaxaCartao(title.taxa_cartao_percentual?.toString() || '');
    setParcelasCartao(title.parcelas_cartao?.toString() || '1');
    setEditPaymentOpen(true);
  };

  const openEstornoDialog = (title) => {
    setEstornoTitle(title);
    setEstornoDialogOpen(true);
  };

  // Função para abrir recibo
  const openReciboDialog = async (title) => {
    const aluno = students.find(s => s.id === title.student_id);
    setReciboTitulo(title);
    setReciboAluno(aluno);
    setReciboDialogOpen(true);
  };

  // Função de impressão
  const handlePrintRecibo = useReactToPrint({
    contentRef: reciboRef,
    documentTitle: `Recibo_${reciboTitulo?.id?.slice(-8) || 'pagamento'}`,
    onAfterPrint: () => {
      toast.success('Recibo enviado para impressão!');
    }
  });

  // Calcular valor líquido
  const calcularValorLiquido = () => {
    const valorBruto = parseFloat(paidValue) || 0;
    const taxa = parseFloat(taxaCartao) || 0;
    if (paymentMethod === 'cartao_credito' || paymentMethod === 'cartao_debito') {
      return valorBruto - (valorBruto * taxa / 100);
    }
    return valorBruto;
  };

  const handleBaixaManual = async () => {
    if (!paidValue || parseFloat(paidValue) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    
    try {
      await api.post(`/titles/${selectedTitle.id}/baixa-manual`, {
        paid_value: parseFloat(paidValue),
        payment_date: paymentDate,
        payment_method: paymentMethod,
        conta_bancaria_id: contaBancariaId && contaBancariaId !== 'none' ? contaBancariaId : null,
        taxa_cartao_percentual: taxaCartao ? parseFloat(taxaCartao) : null,
        parcelas_cartao: parcelasCartao ? parseInt(parcelasCartao) : null,
        observacao: paymentNotes || null
      });
      
      toast.success('Baixa realizada com sucesso!');
      setPayDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar baixa');
    }
  };

  const handleEditarPagamento = async () => {
    if (!paidValue || parseFloat(paidValue) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }
    
    try {
      await api.put(`/titles/${editingTitle.id}/editar-pagamento`, {
        paid_value: parseFloat(paidValue),
        payment_date: paymentDate,
        payment_method: paymentMethod,
        conta_bancaria_id: contaBancariaId && contaBancariaId !== 'none' ? contaBancariaId : null,
        taxa_cartao_percentual: taxaCartao ? parseFloat(taxaCartao) : null,
        parcelas_cartao: parcelasCartao ? parseInt(parcelasCartao) : null,
        observacao: paymentNotes || null
      });
      
      toast.success('Pagamento editado com sucesso!');
      setEditPaymentOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao editar pagamento');
    }
  };

  const handleEstornarPagamento = async () => {
    try {
      await api.post(`/titles/${estornoTitle.id}/estornar-pagamento`);
      toast.success('Pagamento estornado com sucesso!');
      setEstornoDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao estornar pagamento');
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      toast.error('Erro ao acessar câmera');
      setCapturing(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const photo = canvas.toDataURL('image/jpeg', 0.8);
      setReceiptPhoto(photo);
      stopCamera();
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setCapturing(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setReceiptPhoto(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Filtrar títulos
  const filteredTitles = titles.filter(t => {
    const student = students.find(s => s.id === t.student_id);
    const matchesSearch = !searchTerm || 
      student?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.reference_month?.includes(searchTerm);
    const matchesMes = mesFilter === 'all' || t.due_date?.startsWith(mesFilter);
    return matchesSearch && matchesMes;
  });

  // ─────────────────────────────────────────────────────────
  // Selection helpers
  // ─────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const toggleSelectAllFiltered = () => {
    if (filteredTitles.every(t => selectedIds.has(t.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredTitles.map(t => t.id)));
    }
  };

  // Constrói o payload de filtros atual (para "excluir por filtro aplicado")
  const currentFiltersPayload = () => {
    const f = {};
    if (statusFilter !== 'all') f.status = statusFilter;
    if (mesFilter && mesFilter !== 'all') f.reference_month = mesFilter;
    return f;
  };

  // ─────────────────────────────────────────────────────────
  // Open delete confirmation (calls dry-run first)
  // ─────────────────────────────────────────────────────────
  const openDeleteDialog = async (scope, opts = {}) => {
    setDeleteScope(scope);
    setDeleteReason('');
    setDeleteDialogOpen(true);
    setDeletePreview(null);
    setDeleteLoading(true);
    try {
      const body = {};
      if (scope === 'selected') body.title_ids = Array.from(selectedIds);
      else if (scope === 'student') {
        body.student_id = opts.student_id || deleteByStudentId;
        body.filters = currentFiltersPayload();
      } else if (scope === 'filter') {
        body.filters = { ...currentFiltersPayload(), ...(opts.filters || {}) };
      }
      const ep = scope === 'student'
        ? '/financial/titles/delete-by-student?dry_run=true'
        : '/financial/titles/delete-bulk?dry_run=true';
      const res = await api.post(ep, body);
      setDeletePreview(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao gerar prévia';
      toast.error(msg);
      setDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteReason.trim()) { toast.error('Informe o motivo da exclusão'); return; }
    if (!deletePreview || (deletePreview.would_delete || 0) === 0) { toast.error('Nenhum título para excluir'); return; }
    const counts = deletePreview.counts || {};
    if ((counts.paid || 0) > 0) {
      const ok = window.confirm(`Atenção: ${counts.paid} título(s) com pagamento. A exclusão irá estornar o impacto financeiro. Confirma?`);
      if (!ok) return;
    }
    if ((counts.with_nfse || 0) > 0) {
      const ok = window.confirm(`Existe NFS-e vinculada em ${counts.with_nfse} título(s). O cancelamento fiscal deve ser tratado separadamente. Continuar?`);
      if (!ok) return;
    }
    setDeleteLoading(true);
    try {
      const body = { reason: deleteReason };
      if (deleteScope === 'selected') body.title_ids = Array.from(selectedIds);
      else if (deleteScope === 'student') { body.student_id = deleteByStudentId; body.filters = currentFiltersPayload(); }
      else if (deleteScope === 'filter') { body.filters = currentFiltersPayload(); }
      const ep = deleteScope === 'student'
        ? '/financial/titles/delete-by-student?dry_run=false'
        : '/financial/titles/delete-bulk?dry_run=false';
      const res = await api.post(ep, body);
      toast.success(`${res.data.deleted} título(s) excluído(s) (financeiro).`);
      setDeleteDialogOpen(false);
      setSelectedIds(new Set());
      await loadData();
      if (showExcluded) loadExcludedTitles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadExcludedTitles = async () => {
    setLoadingExcluded(true);
    try {
      const res = await api.get('/financial/titles/excluded?limit=500');
      setExcludedTitles(res.data?.items || []);
    } catch {
      setExcludedTitles([]);
    } finally {
      setLoadingExcluded(false);
    }
  };

  const restoreTitles = async (titleIds) => {
    if (!titleIds.length) return;
    const ok = window.confirm(`Restaurar ${titleIds.length} título(s)? O impacto em DRE/Caixa será recomputado.`);
    if (!ok) return;
    try {
      const res = await api.post('/financial/titles/restore', { title_ids: titleIds, reason: 'restauracao manual' });
      toast.success(`${res.data.restored} título(s) restaurado(s).`);
      loadExcludedTitles();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao restaurar');
    }
  };

  useEffect(() => {
    if (showExcluded) loadExcludedTitles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExcluded]);

  return (
    <div className="p-6 space-y-6 notranslate" data-testid="titles-page" translate="no">
      <ModuleHeader kicker="Financeiro" title="Títulos" subtitle="Gerenciar mensalidades e pagamentos" icon={Receipt} testid="titles-header"
        actions={<>
          <Button variant="outline" onClick={handleRecalculateAll} data-testid="titles-recalc">
            <RefreshCw className="w-4 h-4 mr-2" />
            Recalcular Juros
          </Button>
          {/* Exclusão em lote / por filtro / Excluídos — admin/financeiro */}
          {canEdit() && !showExcluded && (
            <>
              <Button
                variant="outline"
                onClick={() => openDeleteDialog('selected')}
                disabled={selectedIds.size === 0}
                data-testid="btn-delete-selected"
                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir selecionados ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                onClick={() => openDeleteDialog('filter')}
                disabled={filteredTitles.length === 0}
                data-testid="btn-delete-filter"
                className="border-amber-200 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              >
                <Filter className="w-4 h-4 mr-2" />
                Excluir filtro ({filteredTitles.length})
              </Button>
            </>
          )}
          <Button
            variant={showExcluded ? 'default' : 'outline'}
            onClick={() => setShowExcluded(s => !s)}
            data-testid="btn-toggle-excluded"
            className={showExcluded ? '' : 'border-slate-200'}
          >
            <Archive className="w-4 h-4 mr-2" />
            {showExcluded ? 'Voltar' : 'Ver excluídos'}
          </Button>
          {canEdit() && !showExcluded && (
            <Button onClick={() => setDialogOpen(true)} data-testid="btn-create-titles" className="dna-mh-primary">
              <Plus className="w-4 h-4 mr-2" />
              Gerar Mensalidades
            </Button>
          )}
        </>} />

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por aluno ou referência..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        
        <div className="flex gap-2 items-center">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select value={mesFilter} onValueChange={setMesFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por mês" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os meses</SelectItem>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-1 bg-muted rounded-lg p-1">
          {['all', 'pendente', 'vencido', 'pago'].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'Todos' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Total de Títulos</div>
            <div className="text-2xl font-bold">{filteredTitles.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Pendentes</div>
            <div className="text-2xl font-bold text-amber-600">
              {filteredTitles.filter(t => t.status === 'pendente').length}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatCurrency(filteredTitles.filter(t => t.status === 'pendente').reduce((sum, t) => sum + (t.original_value - (t.discount_value || 0)), 0))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Vencidos</div>
            <div className="text-2xl font-bold text-red-600">
              {filteredTitles.filter(t => t.status === 'vencido').length}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatCurrency(filteredTitles.filter(t => t.status === 'vencido').reduce((sum, t) => sum + t.original_value + (t.fine_value || 0) + (t.interest_value || 0), 0))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Pagos</div>
            <div className="text-2xl font-bold text-green-600">
              {filteredTitles.filter(t => t.status === 'pago').length}
            </div>
            <div className="text-xs text-green-600 font-medium">
              {formatCurrency(filteredTitles.filter(t => t.status === 'pago').reduce((sum, t) => sum + (t.paid_value || t.original_value - (t.discount_value || 0)), 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {showExcluded ? (
            // ─── Modo Excluídos ───
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Aluno</TableHead>
                  <TableHead>Referência</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Status anterior</TableHead>
                  <TableHead>Excluído em</TableHead>
                  <TableHead>Por</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Avisos</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingExcluded ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Carregando…</TableCell></TableRow>
                ) : excludedTitles.length === 0 ? (
                  <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Nenhum título excluído</TableCell></TableRow>
                ) : excludedTitles.map(t => (
                  <TableRow key={t.id} data-testid={`excluded-row-${t.id}`} className="bg-slate-50">
                    <TableCell></TableCell>
                    <TableCell className="font-medium">{t.student_name || '—'}</TableCell>
                    <TableCell>{t.reference_month || '—'}</TableCell>
                    <TableCell>{t.due_date || '—'}</TableCell>
                    <TableCell>{formatCurrency(t.original_value || 0)}</TableCell>
                    <TableCell><Badge variant="outline">{t.previous_status || '—'}</Badge></TableCell>
                    <TableCell className="text-xs">{t.deleted_at ? new Date(t.deleted_at).toLocaleString('pt-BR') : '—'}</TableCell>
                    <TableCell className="text-xs">{t.deleted_by_email || '—'}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={t.delete_reason || ''}>{t.delete_reason || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {t.had_payment && <Badge className="bg-amber-100 text-amber-800 mr-1">Pgto estornado</Badge>}
                      {t.boleto_canceled && <Badge className="bg-rose-100 text-rose-800 mr-1">Boleto cancelado</Badge>}
                      {t.nfse_alert && <Badge className="bg-purple-100 text-purple-800">NFS-e</Badge>}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => restoreTitles([t.id])} data-testid={`btn-restore-${t.id}`} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Restaurar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {canEdit() && (
                    <Checkbox
                      checked={filteredTitles.length > 0 && filteredTitles.every(t => selectedIds.has(t.id))}
                      onCheckedChange={toggleSelectAllFiltered}
                      data-testid="titles-select-all"
                    />
                  )}
                </TableHead>
                <TableHead>Aluno</TableHead>
                <TableHead>Referência</TableHead>
                <TableHead>Competência acadêmica</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead>Valor Original</TableHead>
                <TableHead>Desconto</TableHead>
                <TableHead>Multa/Juros</TableHead>
                <TableHead>Valor Pago</TableHead>
                <TableHead>Forma Pgto</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8">Carregando...</TableCell>
                </TableRow>
              ) : filteredTitles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    Nenhum título encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredTitles.map((title) => {
                  const student = students.find(s => s.id === title.student_id);
                  const desconto = title.discount_value || 0;
                  const multaJuros = (title.fine_value || 0) + (title.interest_value || 0);
                  const valorFinal = title.status === 'pago' 
                    ? (title.paid_value || title.original_value - desconto)
                    : (title.original_value - desconto + multaJuros);
                  
                  return (
                    <TableRow key={title.id} data-testid={`title-row-${title.id}`}>
                      <TableCell>
                        {canEdit() && (
                          <Checkbox
                            checked={selectedIds.has(title.id)}
                            onCheckedChange={() => toggleSelect(title.id)}
                            data-testid={`title-select-${title.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {student?.name || '-'}
                          {canEdit() && student?.id && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setDeleteByStudentId(student.id); openDeleteDialog('student', { student_id: student.id }); }}
                              className="text-[10px] text-rose-600 hover:text-rose-800 underline"
                              title={`Excluir todos os títulos de ${student.name}`}
                              data-testid={`btn-delete-student-${student.id}`}
                            >
                              excluir todos
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs" data-testid={`titulo-ano-letivo-${title.id}`}>
                        {title.academic_year || title.year || '—'}
                      </TableCell>
                      <TableCell data-testid={`titulo-vencimento-${title.id}`}>
                        {formatDate(title.due_date)}
                        {title.due_date_divergence && (
                          <span className="block text-[10px] text-amber-700"
                            title={`Plano configura ${title.due_date_divergence.configured}`}>
                            divergente do plano
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{formatCurrency(title.original_value)}</TableCell>
                      <TableCell className="text-green-600">
                        {desconto > 0 ? `-${formatCurrency(desconto)}` : '-'}
                      </TableCell>
                      <TableCell className="text-destructive">
                        {multaJuros > 0 ? formatCurrency(multaJuros) : '-'}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {title.status === 'pago' ? (
                          <div>
                            <span className="text-green-600">{formatCurrency(valorFinal)}</span>
                            {title.taxa_cartao_valor > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Líq: {formatCurrency(title.valor_liquido || valorFinal)}
                              </div>
                            )}
                          </div>
                        ) : (
                          formatCurrency(valorFinal)
                        )}
                      </TableCell>
                      <TableCell>
                        {title.payment_method ? (
                          <Badge variant="outline" className="text-xs">
                            {paymentMethods.find(m => m.value === title.payment_method)?.label || title.payment_method}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={getTitleStatusColor(title.status)}>
                          {getTitleStatusLabel(title.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {title.status !== 'pago' && canEdit() && (
                            <Button
                              size="sm"
                              onClick={() => openPayDialog(title)}
                              data-testid="pay-button"
                            >
                              <DollarSign className="w-3 h-3 mr-1" />
                              Baixa
                            </Button>
                          )}
                          {title.status === 'pago' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReciboDialog(title)}
                                title="Imprimir recibo"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                <Printer className="w-3 h-3" />
                              </Button>
                              {canEdit() && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openEditPaymentDialog(title)}
                                    title="Editar pagamento"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => openEstornoDialog(title)}
                                    title="Estornar pagamento"
                                  >
                                    <RotateCcw className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                          {title.receipt_photo && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setViewingTitle(title); setViewReceiptOpen(true); }}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Exclusão Financeira Segura — confirmação com prévia */}
      <Dialog open={deleteDialogOpen} onOpenChange={(o) => { if (!deleteLoading) setDeleteDialogOpen(o); }}>
        <DialogContent className="max-w-2xl" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Exclusão financeira segura
            </DialogTitle>
          </DialogHeader>
          {deleteLoading && !deletePreview && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Calculando impacto…</div>
          )}
          {deletePreview && (
            <div className="space-y-3">
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
                <p className="text-sm font-bold text-rose-900">{deletePreview.would_delete} título(s) serão excluído(s) do financeiro</p>
                <p className="text-xs text-rose-800 mt-0.5">Soft delete · reversível · auditável</p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                {[
                  ['Pagos', deletePreview.counts?.paid, 'text-emerald-700'],
                  ['Abertos', deletePreview.counts?.open, 'text-amber-700'],
                  ['Vencidos', deletePreview.counts?.overdue, 'text-rose-700'],
                  ['c/ Boleto', deletePreview.counts?.with_boleto, 'text-blue-700'],
                  ['c/ Pix', deletePreview.counts?.with_pix, 'text-cyan-700'],
                  ['c/ NFS-e', deletePreview.counts?.with_nfse, 'text-purple-700'],
                ].map(([label, val, cls]) => (
                  <div key={label} className="rounded border border-slate-200 px-2 py-1.5">
                    <p className="text-[10px] uppercase text-slate-500">{label}</p>
                    <p className={`text-lg font-bold ${cls}`}>{val ?? 0}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded bg-slate-50 px-2 py-1.5"><span className="text-slate-500 text-xs">Valor total</span><br/><span className="font-bold">{formatCurrency(deletePreview.counts?.value_total || 0)}</span></div>
                <div className="rounded bg-emerald-50 px-2 py-1.5"><span className="text-emerald-700 text-xs">Valor pago a estornar</span><br/><span className="font-bold text-emerald-700">{formatCurrency(deletePreview.counts?.value_paid || 0)}</span></div>
              </div>
              {deletePreview.samples?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Ver amostras ({deletePreview.samples.length})</summary>
                  <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                    {deletePreview.samples.map(s => (
                      <div key={s.id} className="flex items-center gap-2 px-1 py-0.5 border-b border-slate-100 last:border-0">
                        <span className="font-medium">{s.student_name_masked}</span>
                        <span className="text-slate-500">· ref {s.reference_month || '?'} · {s.due_date || '?'}</span>
                        <span className="ml-auto">{formatCurrency(s.value || 0)}</span>
                        {s.paid && <Badge className="bg-emerald-100 text-emerald-800">pago</Badge>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div>
                <Label htmlFor="delete-reason" className="text-sm">Motivo (obrigatório) *</Label>
                <Textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  placeholder="Ex: erro de lançamento, duplicidade de importação, aluno transferido…"
                  className="mt-1"
                  rows={2}
                  data-testid="delete-reason-input"
                />
              </div>
              <p className="text-[11px] text-slate-500 leading-snug">
                Os títulos selecionados serão marcados como <strong>excluído</strong> e não aparecerão em Caixa, DRE, DRE IA, Painel Global, Executivo, Analytics, Tributário, Fiscal, Lembretes, Cobrança, DNA Connect e relatórios. Pagamentos serão estornados e cobranças (boleto/Pix) canceladas. NFS-e <em>não</em> é apagada — exige cancelamento fiscal separado.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancelar</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteLoading || !deletePreview || (deletePreview.would_delete || 0) === 0 || !deleteReason.trim()}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="delete-confirm-btn"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Confirmar exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Baixa Manual */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Baixa Manual de Título
            </DialogTitle>
          </DialogHeader>
          
          {selectedTitle && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Aluno</p>
                <p className="font-medium">{students.find(s => s.id === selectedTitle.student_id)?.name}</p>
                <p className="text-sm">Ref: {selectedTitle.reference_month} | Venc: {formatDate(selectedTitle.due_date)}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor Pago (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paidValue}
                    onChange={(e) => setPaidValue(e.target.value)}
                    placeholder="0,00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data do Pagamento</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (
                      <SelectItem key={method.value} value={method.value}>
                        <div className="flex items-center gap-2">
                          <method.icon className="w-4 h-4" />
                          {method.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Campos de cartão */}
              {(paymentMethod === 'cartao_credito' || paymentMethod === 'cartao_debito') && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-orange-700">
                    <Percent className="w-4 h-4" />
                    <span className="font-medium text-sm">Taxa da Operadora</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Taxa (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={taxaCartao}
                        onChange={(e) => setTaxaCartao(e.target.value)}
                        placeholder="Ex: 2.99"
                      />
                    </div>
                    {paymentMethod === 'cartao_credito' && (
                      <div className="space-y-1">
                        <Label className="text-xs">Parcelas</Label>
                        <Select value={parcelasCartao} onValueChange={setParcelasCartao}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  {taxaCartao && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Valor líquido: </span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(calcularValorLiquido())}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        (Taxa: {formatCurrency(parseFloat(paidValue || 0) - calcularValorLiquido())})
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Conta bancária */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Conta de Destino
                </Label>
                <Select value={contaBancariaId} onValueChange={setContaBancariaId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma (não especificado)</SelectItem>
                    {contasBancarias.map(conta => (
                      <SelectItem key={conta.id} value={conta.id}>
                        {conta.nome} - {conta.banco_nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Observação (opcional)</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Anotações sobre o pagamento..."
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleBaixaManual} className="bg-green-600 hover:bg-green-700">
              <DollarSign className="w-4 h-4 mr-2" />
              Confirmar Baixa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Editar Pagamento */}
      <Dialog open={editPaymentOpen} onOpenChange={setEditPaymentOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Pagamento
            </DialogTitle>
          </DialogHeader>
          
          {editingTitle && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Aluno</p>
                <p className="font-medium">{students.find(s => s.id === editingTitle.student_id)?.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valor Pago (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={paidValue}
                    onChange={(e) => setPaidValue(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data do Pagamento</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Forma de Pagamento</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethods.map(method => (
                      <SelectItem key={method.value} value={method.value}>
                        <div className="flex items-center gap-2">
                          <method.icon className="w-4 h-4" />
                          {method.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(paymentMethod === 'cartao_credito' || paymentMethod === 'cartao_debito') && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Taxa (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={taxaCartao}
                        onChange={(e) => setTaxaCartao(e.target.value)}
                      />
                    </div>
                    {paymentMethod === 'cartao_credito' && (
                      <div className="space-y-1">
                        <Label className="text-xs">Parcelas</Label>
                        <Select value={parcelasCartao} onValueChange={setParcelasCartao}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Conta de Destino</Label>
                <Select value={contaBancariaId} onValueChange={setContaBancariaId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {contasBancarias.map(conta => (
                      <SelectItem key={conta.id} value={conta.id}>
                        {conta.nome} - {conta.banco_nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Observação</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPaymentOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditarPagamento}>
              <Edit className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Estorno */}
      <Dialog open={estornoDialogOpen} onOpenChange={setEstornoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Estornar Pagamento
            </DialogTitle>
          </DialogHeader>
          
          {estornoTitle && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  Você está prestes a estornar o pagamento deste título. 
                  O título voltará para o status <strong>Pendente</strong>.
                </p>
              </div>
              
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">Aluno</p>
                <p className="font-medium">{students.find(s => s.id === estornoTitle.student_id)?.name}</p>
                <p className="text-sm">
                  Valor pago: <span className="font-medium">{formatCurrency(estornoTitle.paid_value)}</span>
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEstornoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleEstornarPagamento}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Confirmar Estorno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Gerar Mensalidades */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerar Mensalidades</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateMonthly} className="space-y-4">
            <div className="space-y-2">
              <Label>Mês de Referência</Label>
              <Input
                type="month"
                value={formData.reference_month}
                onChange={(e) => setFormData({...formData, reference_month: e.target.value})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Dia de Vencimento</Label>
              <Input
                type="number"
                min="1"
                max="28"
                value={formData.due_day}
                onChange={(e) => setFormData({...formData, due_day: parseInt(e.target.value)})}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                <Calculator className="w-4 h-4 mr-2" />
                Gerar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Ver Comprovante */}
      <Dialog open={viewReceiptOpen} onOpenChange={setViewReceiptOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comprovante de Pagamento</DialogTitle>
          </DialogHeader>
          {viewingTitle?.receipt_photo && (
            <div className="flex justify-center">
              <img 
                src={viewingTitle.receipt_photo} 
                alt="Comprovante" 
                className="max-h-[60vh] object-contain rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Recibo para Impressão */}
      <Dialog open={reciboDialogOpen} onOpenChange={setReciboDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Recibo de Pagamento
            </DialogTitle>
          </DialogHeader>
          
          <div className="border rounded-lg overflow-hidden">
            <ReciboImpressao 
              ref={reciboRef}
              titulo={reciboTitulo}
              aluno={reciboAluno}
              escola={escolaInfo}
              responsavel={null}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReciboDialogOpen(false)}>
              Fechar
            </Button>
            <Button onClick={handlePrintRecibo} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir Recibo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hidden elements for camera */}
      <video ref={videoRef} autoPlay playsInline className="hidden" />
      <canvas ref={canvasRef} className="hidden" />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
}
